import React from "react";
import { autresSites, communeLisible, nomCourtSite } from "./comptesOperateur";
// Aussi affiché dans la fiche utilisateur côté Caf : ses styles viennent avec lui.
import "./operateurs.scss";

// ============================================================
// Sites d'affectation d'un compte d'opérateur (maquette 10) : les sites de la
// structure en cartes à cocher, avec le compteur, « Tout sélectionner » et
// « Tout retirer ». Composant contrôlé : `selection` (identifiants) vit dans
// le parent. En `lectureSeule`, la liste des sites retenus.
// Règle du 20/10/2026 : un compte d'opérateur ne reçoit que ses sites
// (`vueLimitee`). Les sites du compte qu'il ne voit pas restent dans la
// sélection, sont signalés, et ni « Tout retirer » ni « Tout sélectionner »
// n'y touchent : l'API les conserve de toute façon.
// ============================================================

const ChoixSitesCompte = ({
  sites = [],
  selection = [],
  onChange,
  nomStructure = "",
  erreur = null,
  lectureSeule = false,
  vueLimitee = false,
  idPrefix = "compte",
  // Sur la fiche, la section porte déjà le titre « Sites ».
  avecLibelle = true,
}) => {
  const visibles = sites.map((s) => s.id);
  const caches = vueLimitee ? selection.filter((id) => !visibles.includes(id)) : [];
  const deStructure = nomStructure ? ` ${/^[aeiouyàâéèêëîïôöûü]/i.test(nomStructure) ? "d'" : "de "}${nomStructure}` : "";

  if (lectureSeule) {
    const retenus = sites.filter((s) => selection.includes(s.id));
    return (
      <div className="choix-sites-compte">
        {retenus.length ? (
          <ul className="choix-sites-compte__lecture">
            {retenus.map((s) => (
              <li key={s.id}>
                <i className="fa fa-map-marker-alt" aria-hidden="true" />
                {nomCourtSite(s, nomStructure)}
              </li>
            ))}
          </ul>
        ) : (
          !caches.length && <p className="fiche-operateur__secondaire mb-0">Aucun site.</p>
        )}
        {caches.length > 0 && (
          <p className="choix-sites-compte__autres">
            {retenus.length ? "Et" : "Rattaché à"} {autresSites(caches.length)}
            {deStructure}, que vous ne voyez pas.
          </p>
        )}
      </div>
    );
  }

  const basculer = (id) =>
    onChange(selection.includes(id) ? selection.filter((s) => s !== id) : [...selection, id]);
  const idErreur = `${idPrefix}-erreur-sites`;

  return (
    <fieldset className="choix-sites-compte" aria-describedby={erreur ? idErreur : undefined}>
      <legend className="sr-only">Sites</legend>
      <div className="choix-sites-compte__barre">
        {avecLibelle && (
          <span className="choix-sites-compte__libelle" aria-hidden="true">
            Sites
          </span>
        )}
        <span className="choix-sites-compte__compteur">
          {selection.filter((id) => visibles.includes(id)).length} / {sites.length}
        </span>
        {sites.length > 1 && (
          <span className="choix-sites-compte__actions">
            <button type="button" className="choix-sites-compte__lien" onClick={() => onChange([...caches, ...visibles])}>
              Tout sélectionner
            </button>
            <span aria-hidden="true"> · </span>
            <button type="button" className="choix-sites-compte__lien" onClick={() => onChange(caches)}>
              Tout retirer
            </button>
          </span>
        )}
      </div>
      <p className="modale-operateur__aide choix-sites-compte__aide">
        Les calendriers sur lesquels l'utilisateur reçoit : il ne voit que ces sites et ce qui s'y rattache.{" "}
        {vueLimitee ? "Seuls vos sites sont proposés." : `Seuls les sites${deStructure} sont proposés.`}
      </p>
      {sites.length ? (
        <div className="choix-compacts">
          {sites.map((site) => {
            const id = `${idPrefix}-site-${site.id}`;
            const coche = selection.includes(site.id);
            return (
              <label key={site.id} htmlFor={id} className={`choix-compacte${coche ? " is-checked" : ""}`}>
                <input type="checkbox" id={id} name="sites" value={site.id} checked={coche} onChange={() => basculer(site.id)} />
                <span>
                  {site.nom}
                  {site.ville && <span className="choix-compacte__detail">{communeLisible(site.ville)}</span>}
                </span>
              </label>
            );
          })}
        </div>
      ) : (
        <p className="fiche-operateur__secondaire mb-0">Aucun site pour cette structure : la Caf ajoute d'abord un site.</p>
      )}
      {caches.length > 0 && (
        <p className="choix-sites-compte__autres">
          Aussi rattaché à {autresSites(caches.length)}
          {deStructure}, que vous ne gérez pas : {caches.length > 1 ? "ils restent rattachés" : "il reste rattaché"}.
        </p>
      )}
      {erreur && (
        <small role="alert" id={idErreur} className="form-text text-danger">
          {erreur}
        </small>
      )}
    </fieldset>
  );
};

export default ChoixSitesCompte;
